import type { FunctionComponent } from 'react'
import Markdown from 'react-markdown'
import CopyToClipboardButton from './CopyToClipboardButton'
import { useRef } from 'react'

import { removeMarkdown } from '../../utils/removeFormatting'
import { cn } from '@playbooks/ui/lib/utils'

interface MarkdownWithCopyButtonProps {
  markdownText: string
  className?: string
}

/**
 * Component that renders markdown-formatted text with a "Copy to clipboard" button.
 * @story User Story 10482: Generic Prompt AI widget: select output mode: add or replace
 */
const MarkdownWithCopyButton: FunctionComponent<MarkdownWithCopyButtonProps> = ({ markdownText, className }) => {
  // Refs
  const wrapperRef = useRef<HTMLDivElement>(null)

  return (
    <div
      // We need to provide inline styles here to avoid specifying tailwindCSS classes as !important
      // because of the CSS isolation applied to this component (isolated-html-content).
      style={{
        lineHeight: '20px',
        fontSize: '14px',
      }}
      className={cn('isolated-html-content dark:text-[#FFFFFFCC] text-[#000000AA]', className)}
      ref={wrapperRef}
    >
      <Markdown
        components={{
          // Replace a special code block with a "Copy to clipboard" button
          // so that is appears at the end of the generated text
          // (https://github.com/remarkjs/react-markdown#use-custom-components-syntax-highlight)
          // @ts-ignore
          code(props) {
            const { children, className, node, ...rest } = props
            const match = /copy_to_clipboard_button/.exec(String(children) || '')
            return match ? (
              <CopyToClipboardButton
                text={removeMarkdown(markdownText || '')}
                richText={() => wrapperRef.current?.innerHTML || ''}
                className="text-l pl-[5px] align-middle"
              />
            ) : (
              // @ts-ignore
              <code {...rest} className={className}>
                {children}
              </code>
            )
          },
        }}
      >
        {`${markdownText}`}
      </Markdown>
    </div>
  )
}

export default MarkdownWithCopyButton
